import React, { useState, useEffect } from 'react';
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StudioInfoForm from '@/components/settings/StudioInfoForm';
import BillingSettingsForm from '@/components/settings/BillingSettingsForm';
import ClassDefaultsForm from '@/components/settings/ClassDefaultsForm';
import StudioLogo from '@/components/settings/StudioLogo';
import NavigationSettings from '@/components/settings/NavigationSettings';
import { Skeleton } from '@/components/ui/skeleton';
import LocationsManager from '@/components/settings/LocationsManager';
import StripeConnectSettings from '@/components/settings/StripeConnectSettings';
import { toast } from 'sonner';
import { Settings as SettingsIcon, User, Bell, CreditCard, Crown, Mail } from 'lucide-react';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { Organization, BillingSettings, ClassDefaults, Location } from '@/types/organizationTypes';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FeatureGate, useFeatureAccess } from '@/components/billing/FeatureGate';
import { PremiumContentWrapper } from '@/components/billing/PremiumContentWrapper';
import { Hive360SettingsPanel } from '@/components/hive360/Hive360SettingsPanel';
import HivePackSettings from '@/components/settings/HivePackSettings';
import DailyReportSettings from '@/components/settings/DailyReportSettings';
import { OrganizationType } from '@/types/organizationCustomizationTypes';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';

interface StudioSettings {
  studio_name: string;
  contact_email: string;
  contact_phone: string;
  timezone: string;
}

const Settings = () => {
  const { user, userRole, organizationId, loading, ready } = useAuth();
  const [studioLogo, setStudioLogo] = useState<string | null>(null);
  const [studioSettings, setStudioSettings] = useState<StudioSettings>({
    studio_name: '',
    contact_email: '',
    contact_phone: '',
    timezone: ''
  });
  const [billingSettings, setBillingSettings] = useState<BillingSettings>({});
  const [classDefaults, setClassDefaults] = useState<ClassDefaults>({});
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const { hasAccess: hasHive360Access, loading: featureLoading } = useFeatureAccess('hive360');
  const { isHiveShareOnly } = useSubscriptionAccess();

  // Get organization ID from user metadata if not available from auth context
  const effectiveOrgId = organizationId || (user?.user_metadata?.organization_id as string | undefined);
  
  // Use for debugging only
  useEffect(() => {
    if (!effectiveOrgId) {
      console.log("Settings: No organization ID found in auth context");
      
      // Log user metadata for debugging
      if (user?.user_metadata) {
        console.log("Settings: User metadata:", user.user_metadata);
      }
    } else {
      console.log("Settings: Using organization ID:", effectiveOrgId);
    }
  }, [effectiveOrgId, user]);
  
  const { organization, error, updateBillingSettings, updateClassDefaults, refreshOrganizationData } = useOrganizationSettings(effectiveOrgId);

  useEffect(() => {
    if (effectiveOrgId) {
      // Try to refresh organization data if there's an error
      if (error) {
        console.error("Error loading organization:", error);
        refreshOrganizationData().catch(err => {
          console.error("Failed to refresh organization data:", err);
          setLoadError("Unable to load organization settings. Please try again later.");
        });
      }
    } else {
      console.error("Settings: No effective organization ID available");
      setLoadError("Organization ID not found. Please make sure you're logged in with the correct account.");
    }
  }, [effectiveOrgId, error, refreshOrganizationData]);

  useEffect(() => {
    const fetchLocations = async () => {
      if (!effectiveOrgId) {
        console.log("Cannot fetch locations: No organization ID");
        return;
      }
      
      try {
        console.log("Fetching locations for organization:", effectiveOrgId);
        const { data, error } = await supabase
          .from('locations')
          .select('*')
          .eq('organization_id', effectiveOrgId);
          
        if (error) throw error;
        
        console.log(`Loaded ${data.length} locations`);
        setLocations(data as Location[]);
      } catch (error) {
        console.error('Error loading locations:', error);
        toast.error('Failed to load locations');
      }
    };
    
    fetchLocations();
  }, [effectiveOrgId]);

  useEffect(() => {
    if (organization) {
      console.log("Organization loaded:", organization.name);
      setStudioLogo(organization.logo_url || null);
      
      setStudioSettings({
        studio_name: organization.name,
        contact_email: organization.contact_email || '',
        contact_phone: organization.contact_phone || '',
        timezone: organization.timezone || ''
      });
      
      if (organization.billing_settings) {
        setBillingSettings(organization.billing_settings as BillingSettings);
      }
      
      if (organization.class_defaults) {
        setClassDefaults(organization.class_defaults as ClassDefaults);
      }
      
      // Set organization type from database
      if (organization.organization_type) {
        setOrganizationType(organization.organization_type as OrganizationType);
      }
    }
  }, [organization]);

  // Check if the user is a superadmin
  useEffect(() => {
    if (!ready) return; // Only run when auth is ready
    const checkSuperAdminStatus = async () => {
      if (!user) return;
      
      // Check if email matches allowed superadmin emails
      const allowedEmails = [
        'hallryan97@gmail.com',
        'ryan.hall@gtmpact.com',
        'ryan@gtmpact.com'
      ];
      
      if (user.email && allowedEmails.includes(user.email)) {
        setIsSuperAdmin(true);
        return;
      }
      
      // If email doesn't match, check for superuser flag in profiles
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('is_superuser')
          .eq('id', user.id)
          .maybeSingle();
          
        if (!error && data) {
          setIsSuperAdmin(!!data.is_superuser);
        }
      } catch (error) {
        console.error('Error checking superuser status:', error);
      }
    };
    
    checkSuperAdminStatus();
  }, [user, ready]);

  if (loading || featureLoading) {
    return (
      <div className="space-y-8 p-6">
        <Skeleton className="h-12 w-48" />
        <div className="grid gap-8">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (loadError || error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {loadError || "An error occurred loading settings. Please try again."}
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button onClick={() => refreshOrganizationData()}>
            Retry Loading Settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Settings"
        subtitle="Configure your hive's settings and preferences"
        icon={SettingsIcon}
        actions={
          <Link to="/admin/parent-portal-settings">
            <Button variant="outline" className="gap-2">
              <User className="h-4 w-4" />
              Parent Portal Settings
            </Button>
          </Link>
        }
      />
      
      <PageContainer>
        <Tabs defaultValue="general">
        <TabsList className="mb-8">
          <TabsTrigger value="general">General</TabsTrigger>
          {!isHiveShareOnly && <TabsTrigger value="hivepack">HivePack</TabsTrigger>}
          {!isHiveShareOnly && <TabsTrigger value="navigation">Navigation</TabsTrigger>}
          {!isHiveShareOnly && <TabsTrigger value="notifications">Notifications</TabsTrigger>}
          {!isHiveShareOnly && <TabsTrigger value="billing">Billing</TabsTrigger>}
          {!isHiveShareOnly && <TabsTrigger value="payments">Payments</TabsTrigger>}
          {!isHiveShareOnly && <TabsTrigger value="classes">Classes</TabsTrigger>}
          {!isHiveShareOnly && <TabsTrigger value="locations">Locations</TabsTrigger>}
          {!isHiveShareOnly && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="hive360" className={!hasHive360Access ? "opacity-75" : ""}>
                    Hive360
                    {!hasHive360Access && <Crown className="h-3 w-3 ml-1 text-blue-500" />}
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {hasHive360Access 
                      ? "Embed class enrollment widgets on your website"
                      : "Advanced feature - Upgrade to unlock Hive360"
                    }
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TabsTrigger value="subscription">Subscription Plan</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="space-y-8">
          <StudioLogo 
            organizationId={effectiveOrgId || ''} 
            studioLogo={studioLogo} 
            setStudioLogo={setStudioLogo} 
          />
          
          <StudioInfoForm 
            organizationId={effectiveOrgId}
            studioSettings={studioSettings}
            setStudioSettings={setStudioSettings}
          />
        </TabsContent>
        
        {!isHiveShareOnly && (
          <TabsContent value="hivepack" className="space-y-8">
            <HivePackSettings 
              organizationId={effectiveOrgId || ''} 
              currentOrganizationType={organizationType}
            />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="navigation" className="space-y-8">
            <NavigationSettings />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="notifications" className="space-y-8">
            <DailyReportSettings />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="billing" className="space-y-8">
            <BillingSettingsForm 
              billingSettings={billingSettings} 
              updateBillingSettings={(settings) => {
                return updateBillingSettings(settings).then((updatedOrg) => {
                  setBillingSettings(prev => ({ ...prev, ...settings }));
                  return updatedOrg;
                });
              }}
            />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="payments" className="space-y-8">
            <StripeConnectSettings 
              organization={organization} 
              organizationId={effectiveOrgId || ''} 
            />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="classes" className="space-y-8">
            <ClassDefaultsForm 
              organizationId={effectiveOrgId || ''} 
              classDefaults={classDefaults}
              setClassDefaults={setClassDefaults}
            />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="locations" className="space-y-8">
            <LocationsManager 
              locations={locations}
              setLocations={setLocations}
              organizationId={effectiveOrgId || ''}
            />
          </TabsContent>
        )}
        
        {!isHiveShareOnly && (
          <TabsContent value="hive360" className="space-y-8">
            <PremiumContentWrapper hasAccess={hasHive360Access} featureName="Hive360">
              <Hive360SettingsPanel />
            </PremiumContentWrapper>
          </TabsContent>
        )}
        
        <TabsContent value="subscription" className="space-y-8">
          <div className="flex justify-end mb-4 gap-2">
            {/* For superadmins: Manage Subscription Plans */}
            {isSuperAdmin && (
              <Link to="/superadmin/subscription-management">
                <Button variant="outline" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  Manage Subscription Plans
                </Button>
              </Link>
            )}
            
            {/* For bizadmins: View/Manage Your Subscription */}
            {!isSuperAdmin && (
              <Link to="/admin/subscription-center">
                <Button variant="default" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  View Your Subscription
                </Button>
              </Link>
            )}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Subscription Plan</CardTitle>
              <CardDescription>
                {isSuperAdmin 
                  ? "Manage subscription plans for all customers" 
                  : "View and manage your subscription plan"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSuperAdmin ? (
                <p>
                  As a superadmin, you can create and manage subscription plans for your customers.
                  Use the "Manage Subscription Plans" button to access the subscription management dashboard.
                </p>
              ) : (
                <p>
                  View details about your current subscription and manage your billing information.
                  Click "View Your Subscription" to see your current plan and billing details.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>
      </PageContainer>
    </div>
  );
};

export default Settings;
